import React, { useMemo, useState } from 'react';

import type { GetBioacousticsSampleDataQuery } from '@/__generated__/graphql';

type BioacousticsData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData'];

import { BioacousticsTooltip } from './BioacousticsTooltip.js';

const PALETTE = [
  '#006d6a', '#db5012', '#4083ba', '#265518', '#3e1915',
  '#338a88', '#e27341', '#669cc8', '#517746', '#654744',
  '#80b6b5', '#f0b89f', '#8cb5d6', '#a8bba3', '#8b7573',
];

const OTHER_THRESHOLD_PCT = 1;

interface Segment {
  key: string;
  latinTaxa: string | null;
  commonName: string | null;
  imageUrl: string | null;
  count: number;
  pct: number;
  centerPct: number;
  color: string;
  isOther: false;
}

interface OtherSegment {
  key: 'other';
  latinTaxa: null;
  commonName: null;
  imageUrl: null;
  count: number;
  pct: number;
  centerPct: number;
  color: string;
  isOther: true;
  speciesCount: number;
}

interface Props {
  data: BioacousticsData | undefined;
}

export function BirdSpeciesBar({ data }: Props): React.JSX.Element {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mouse, setMouse] = useState<{ x: number; y: number } | null>(null);

  const segments = useMemo((): Array<Segment | OtherSegment> => {
    if (!data) return [];
    const counts = new Map<string, { count: number; commonName: string | null; imageUrl: string | null }>();
    for (const obs of data.birdObservations) {
      const key = obs.latinTaxa;
      const existing = counts.get(key);
      if (existing) {
        existing.count++;
        if (!existing.imageUrl && obs.imgUrl) existing.imageUrl = obs.imgUrl;
      } else {
        counts.set(key, { count: 1, commonName: obs.commonName ?? null, imageUrl: obs.imgUrl ?? null });
      }
    }
    const total = Array.from(counts.values()).reduce((s, v) => s + v.count, 0);
    if (total === 0) return [];

    const sorted = Array.from(counts.entries()).sort((a, b) => b[1].count - a[1].count);

    const main: Segment[] = [];
    let otherCount = 0;
    let otherSpecies = 0;

    for (const [latinTaxa, { count, commonName, imageUrl }] of sorted) {
      const pct = (count / total) * 100;
      if (pct < OTHER_THRESHOLD_PCT) {
        otherCount += count;
        otherSpecies++;
      } else {
        main.push({ key: latinTaxa, latinTaxa, commonName, imageUrl, count, pct, centerPct: 0, color: PALETTE[main.length % PALETTE.length] ?? '#787664', isOther: false });
      }
    }

    let cumulative = 0;
    const result: Array<Segment | OtherSegment> = main.map(seg => {
      const centerPct = cumulative + seg.pct / 2;
      cumulative += seg.pct;
      return { ...seg, centerPct };
    });

    if (otherCount > 0) {
      const pct = (otherCount / total) * 100;
      result.push({ key: 'other', latinTaxa: null, commonName: null, imageUrl: null, count: otherCount, pct, centerPct: cumulative + pct / 2, color: '#787664', isOther: true, speciesCount: otherSpecies });
    }

    return result;
  }, [data]);

  if (segments.length === 0) return <></>;

  const hovered = hoveredIndex !== null ? segments[hoveredIndex] : null;

  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Detections by species</p>
      <div className="relative">
        {/* Tooltip */}
        {hovered && mouse && (
          <BioacousticsTooltip x={mouse.x} y={mouse.y}>
            {hovered.isOther ? (
              <p style={{ margin: '0 0 2px', fontWeight: 600 }}>{hovered.speciesCount} other species</p>
            ) : hovered.commonName ? (
              <>
                <p style={{ margin: '0 0 2px', fontWeight: 600 }}>{hovered.commonName}</p>
                <p style={{ margin: '0 0 2px', opacity: 0.6, fontStyle: 'italic' }}>{hovered.latinTaxa}</p>
              </>
            ) : (
              <p style={{ margin: '0 0 2px', fontWeight: 600, fontStyle: 'italic' }}>{hovered.latinTaxa}</p>
            )}
            <p style={{ margin: '2px 0 0', opacity: 0.7 }}>{hovered.count.toLocaleString()} detections · {hovered.pct.toFixed(1)}%</p>
          </BioacousticsTooltip>
        )}

        {/* Bar */}
        <div
          className="flex w-full overflow-hidden rounded-sm"
          style={{ height: 28 }}
          onMouseLeave={() => { setHoveredIndex(null); setMouse(null); }}
        >
          {segments.map((seg, i) => (
            <div
              key={seg.key}
              className="cursor-default transition-opacity"
              style={{
                width: `${seg.pct}%`,
                backgroundColor: seg.color,
                opacity: hoveredIndex === null ? 1 : hoveredIndex === i ? 1 : 0.4,
              }}
              onMouseEnter={(e) => { setHoveredIndex(i); setMouse({ x: e.clientX, y: e.clientY }); }}
              onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}
            />
          ))}
        </div>
      </div>
      <p className="text-xs mt-2 text-brand-teal-dark-300">
        Evenness across species drives the score
      </p>
    </div>
  );
}
